import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment, useCallback } from 'react';
import { GridItem, Switch, Typography, WithInfoIcon, useOverlayContext } from 'seagull';
import { useIntegration } from '~/hooks/useIntegration';
import { useLocaleKeys } from '~/i18n';
import { selectors } from '~/store/state-selectors';
import { useStore, useStoreActions } from '~/store/store-context';
import { getAccountingDetails } from '../accounting.utils';
export const AccountingAdvancedSettings = (param)=>{
    let { accounting, isLinked } = param;
    const accountingIntegration = useStore((state)=>selectors.integration.accountingIntegration(state, accounting));
    const { t: commonTexts } = useLocaleKeys('common');
    const { setIntegration, logBI } = useStoreActions();
    const { showErrorToast, startLoader, stopLoader } = useOverlayContext();
    const { updateSettings: updateAccountingSettings } = useIntegration(accounting);
    const accountingFullName = getAccountingDetails(accounting).fullName;
    const vendorId = useStore(selectors.user.businessId);
    const userId = useStore(selectors.user.id);
    const updateSettings = useCallback(async (updated)=>{
        startLoader();
        const { success } = await updateAccountingSettings(updated.settings());
        if (success) {
            setIntegration(updated);
        } else {
            showErrorToast(commonTexts.toasts.failedToUpdateSettings(), 'failed-to-update-settings-error-toast');
        }
        stopLoader();
    }, [
        updateAccountingSettings,
        setIntegration,
        startLoader,
        stopLoader,
        showErrorToast
    ]);
    const toggleClassOverrides = (classOverride)=>{
        if (!accountingIntegration) return;
        const updated = accountingIntegration.setClassTrackingAgreementOverride(classOverride);
        updateSettings(updated);
    };
    const toggleAutoSyncInvoices = (autoSync)=>{
        if (!accountingIntegration) return;
        const updated = accountingIntegration.setAutomaticInvoicesSync(autoSync);
        updateSettings(updated);
    };
    const toggleDisplayBillableTimeActivities = (displayBillableTimeActivities)=>{
        if (!accountingIntegration) return;
        const updated = accountingIntegration.setDisplayBillableTimeActivities(displayBillableTimeActivities);
        updateSettings(updated);
    };
    const getOptions = (logBI)=>{
        if (!accountingIntegration || !accountingIntegration.settings()) return [];
        const settingsConfig = {
            automaticInvoicesSync: {
                id: 'automaticInvoicesSync',
                name: "Manually sync items to ".concat(accountingFullName, " (not recommended)"),
                info: "Invoices, payments and deposits will not be synced automatically to ".concat(accountingFullName, ". A manual sync option will be available on each item."),
                isInverted: true,
                testId: 'sync-items-manually-switch',
                action: toggleAutoSyncInvoices
            },
            classTrackingAgreementOverride: {
                id: 'classTrackingAgreementOverride',
                name: "Allow assigning classes to services on a per-agreement basis",
                info: 'If this feature is activated, the default Class assigned to services can be overridden on a per-agreement basis.',
                isInverted: false,
                testId: 'class-overrides-switch',
                action: toggleClassOverrides
            },
            displayBillableTimeActivities: {
                id: 'displayBillableTimeActivities',
                name: 'Display billable time activities',
                info: "Billable time activities from ".concat(accountingFullName, " will be displayed in Anchor when this setting is enabled."),
                isInverted: false,
                testId: 'display-billable-time-activities-switch',
                action: (displayBillableTimeActivities)=>{
                    if (!displayBillableTimeActivities) logBI(12018, {
                        vendorId,
                        userId
                    });
                    toggleDisplayBillableTimeActivities(displayBillableTimeActivities);
                }
            }
        };
        const options = Object.values(settingsConfig).filter((setting)=>{
            if (setting.id === 'displayBillableTimeActivities') return setting.id in accountingIntegration.settings();
            return setting.id in accountingIntegration.settings();
        });
        return options;
    };
    const settings = getOptions(logBI);
    if (!accountingIntegration) return null;
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: settings.map((setting)=>/*#__PURE__*/ _jsxs(Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(GridItemLeft, {
                        children: /*#__PURE__*/ _jsx(WithInfoIcon, {
                            info: setting.info,
                            children: /*#__PURE__*/ _jsx(Typography, {
                                children: setting.name
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(GridItemRight, {
                        children: /*#__PURE__*/ _jsx(Switch, {
                            checked: setting.isInverted ? !accountingIntegration.settingValue(setting.id) : accountingIntegration.settingValue(setting.id),
                            disabled: !isLinked,
                            testId: setting.testId,
                            onChange: (_, checked)=>setting.isInverted ? setting.action(!checked) : setting.action(checked)
                        })
                    })
                ]
            }, setting.id))
    });
};
const GridItemRight = (param)=>{
    let { children } = param;
    return /*#__PURE__*/ _jsx(GridItem, {
        span: 2,
        justify: "center",
        children: children
    });
};
const GridItemLeft = (param)=>{
    let { children } = param;
    return /*#__PURE__*/ _jsx(GridItem, {
        span: 10,
        children: children
    });
};
